import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Row, Col, Button, Divider, Skeleton } from 'antd';
import { UpOutlined, DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';

import { Title } from '@/components/Typography';
import { ActionPlanCard } from './ActionPlanCard';
import { ActionPlanModal } from '@/components/ActionPlan';
import { ActionPlanIcon } from '@/assets/icons/ActionPlanIcon';
import { useApplicationContext } from '@/context/v1/Application/context';
import { RiskBadge } from '@/components/views/BeraJob/components/RiskBadge';
import { useGetBeraJobSummaryActionPlans } from '@/hooks/useGetBeraJobSummaryActionPlans';
import * as S from './styles';

interface ActionPlansProps {
	beraJobSummaryId: string;
	fileId: string;
	workstationId: string;
	rpn: number;
}

export function ActionPlans({ beraJobSummaryId = '', fileId = '', workstationId = '', rpn = 0 }: Readonly<ActionPlansProps>): JSX.Element {

	const { organization, company } = useApplicationContext();
	
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [resetForm, setResetForm] = useState<boolean>(false);
	const [actionPlanId, setActionPlanId] = useState<string | null>(null);
	const [isActionPlanModalOpen, setIsActionPlanModalOpen] = useState<boolean>(false);

	const {
		isLoading,
		data: actionPlans,
		isFetching,
		refetch
	} = useGetBeraJobSummaryActionPlans({
		organization_id: organization?.id,
		company_id: company?.id,
		id: beraJobSummaryId
	});

	const totalPages = actionPlans?.length || 0;
	const currentActionPlan = actionPlans?.[currentPage - 1];

	function handleCreateActionPlan(): void {
		setActionPlanId(null);
		setIsActionPlanModalOpen(true);
		setResetForm(true);
	}

	function handleCloseModal(): void {
		setIsActionPlanModalOpen(false);
		setActionPlanId(null);
		refetch();
	}

	function handlePreviousPage(): void {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1);
		}
	}

	function handleNextPage(): void {
		if (currentPage < totalPages) {
			setCurrentPage(currentPage + 1);
		}
	}

	function handlePageClick(page: number): void {
		setCurrentPage(page);
	}

	function handleMoreActions(id: string): void {
		setActionPlanId(id);
		setIsActionPlanModalOpen(true);
		setResetForm(false);
	}

	function handleCreateOrUpdate(): void {
		refetch();
		setIsActionPlanModalOpen(false);
		setActionPlanId(null);
	}

	const renderPagination = () => {
		if (totalPages <= 1) return null;

		const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

		return (
			<S.PaginationContainer>
				<S.PaginationButtons>
					<S.PaginationArrow $disabled={currentPage === 1} onClick={handlePreviousPage}>
						<LeftOutlined style={{ fontSize: 12, color: currentPage === 1 ? '#e6e6e6' : '#262626' }} />
					</S.PaginationArrow>
					{pages.map((page) => (
						<S.PaginationNumber
							key={page}
							$active={page === currentPage}
							onClick={() => handlePageClick(page)}
						>
							{page}
						</S.PaginationNumber>
					))}
					<S.PaginationArrow $disabled={currentPage === totalPages} onClick={handleNextPage}>
						<RightOutlined
							style={{ fontSize: 12, color: currentPage === totalPages ? '#e6e6e6' : '#262626' }}
						/>
					</S.PaginationArrow>
				</S.PaginationButtons>
			</S.PaginationContainer>
		);
	};

	return (
		<>
			<Row justify="center" onClick={() => setIsOpen(!isOpen)} style={{ cursor: 'pointer', marginTop: '2rem' }}>
				<Col span={14}>
					<Row>
						<Title level={5}>Action plans</Title>
					</Row>
				</Col>
				<Col span={10}>
					<Row justify="end" gutter={10} align="middle" style={{ gap: '1rem' }}>
						<Button
							type="primary"
							icon={<ActionPlanIcon fill="white" />}
							onClick={(e) => {
								e.stopPropagation();
								handleCreateActionPlan();
							}}
							style={{
								justifyContent: 'center',
								alignItems: 'center',
								display: 'flex',
								gap: '0.5rem'
							}}
						>
							{I18n.get('Add action plan')}
						</Button>
						{isOpen ? <UpOutlined /> : <DownOutlined />}
					</Row>
				</Col>
			</Row>
			<Divider />
			{isOpen && (
				<Row justify="center" style={{ marginTop: '1rem' }}>
					<Col span={24}>
						{(isLoading || isFetching) && <Skeleton active />}
						{!isLoading && !isFetching && currentActionPlan && (
							<>
								<ActionPlanCard data={currentActionPlan} onMoreActions={handleMoreActions} />
								{renderPagination()}
							</>
						)}
						{!isLoading && !isFetching && (!actionPlans || actionPlans.length === 0) && (
							<S.EmptyState>{I18n.get('No action plans')}</S.EmptyState>
						)}
					</Col>
				</Row>
			)}
			<ActionPlanModal
				open={isActionPlanModalOpen}
				onClose={handleCloseModal}
				table_origin="bera_job_summaries"
				table_origin_id={beraJobSummaryId}
				file_id={fileId}
				workstation_id={workstationId}
				origin_name={'BERA'}
				setActionPlanId={setActionPlanId}
				risk={<RiskBadge taskRPN={rpn} />}
				action_plan_id={actionPlanId}
				onCreateOrUpdate={handleCreateOrUpdate}
				resetForm={resetForm}
			/>
		</>
	);
}
